import React, { useCallback, FC, useId, useMemo } from "react"
import { AspectRatio, Box, Flex, Image } from "@chakra-ui/react"
import { SingleSelect } from "../../shared/SingleSelect/SingleSelect"
import type { NextPageCb } from "../../../hooks/useNextQuiz"
import { SelectLayoutEnum } from "../../../api/QuestionType"
import { TTString } from "../../shared/AttributedString"
import { BasePageHeader } from "~/components/shared/BasePageHeader/BasePageHeader"
import { SingleVariantQuestionScreen } from "~/generated/interview_service"

const HEADER_IMAGE_RATIO = 21 / 9

export const QuestionImage: FC<{ src: string }> = ({ src }) => (
  <AspectRatio ratio={HEADER_IMAGE_RATIO} marginBottom={2}>
    <Image src={src} borderBottomRadius="xl" align="center top" objectFit="cover" />
  </AspectRatio>
)

type Variant = {
  title: string
  description?: string | undefined
  image?: string | undefined
  icon?: string | undefined
  deselect_other_variants?: boolean
}
export const QuestionPageSingleSelect: FC<{
  title: TTString
  image?: string | undefined
  description?: string | undefined
  upTitle?: TTString
  layout: SelectLayoutEnum
  variantsTitles: SingleVariantQuestionScreen["variants"]
  variantProperties: SingleVariantQuestionScreen["variant_properties"]
  next: NextPageCb
}> = ({
  title,
  upTitle,
  description,
  variantsTitles,
  variantProperties,
  image,
  layout = SelectLayoutEnum.ONE_COLUMN,
  next,
}) => {
  const variants = usePrepareVariants({ variantsTitles, variantProperties, layout })
  const id = useId()
  const onSelect = useCallback(
    (variant: string) => {
      next([variant])
    },
    [next]
  )

  return (
    <Flex direction="column" minHeight="100%" paddingTop={image ? 0 : 12}>
      {image && <QuestionImage src={image} />}

      <Box marginX={6}>
        <BasePageHeader
          marginTop={4}
          marginBottom={6}
          title={title}
          upTitle={upTitle}
          subTitle={description}
        />
        <SingleSelect key={id} variants={variants} onChange={onSelect} layout={layout} />
      </Box>
    </Flex>
  )
}

function usePrepareVariants({
  variantsTitles,
  variantProperties,
  layout,
}: {
  variantsTitles: SingleVariantQuestionScreen["variants"]
  variantProperties: SingleVariantQuestionScreen["variant_properties"]
  layout: SingleVariantQuestionScreen["layout"]
}): Variant[] {
  return useMemo(
    () =>
      variantsTitles.map((title) => {
        const rest = variantProperties?.[title]
        if (rest) {
          return {
            title,
            image:
              layout === SelectLayoutEnum.ONE_COLUMN_IMAGES ||
              layout === SelectLayoutEnum.TWO_COLUMNS_IMAGES ||
              layout === SelectLayoutEnum.TWO_COLUMNS_IMAGES_TRANSPARENT
                ? rest.image_link
                : undefined,
            icon:
              layout === SelectLayoutEnum.ONE_COLUMN || layout === SelectLayoutEnum.TWO_COLUMNS
                ? rest.image_link
                : undefined,
            description: rest.description,
            deselect_other_variants: rest.deselect_other_variants ?? false,
          }
        }
        return { title }
      }),
    [layout, variantProperties, variantsTitles]
  )
}
